/**
 * Purpose:
 *   Loading spinner component with animated circular arc.
 *
 * Notes:
 *   - Uses SVG with CSS animations
 *   - Configurable size and stroke thickness
 *   - Accessible with role="img" and aria-label
 */

import React from 'react';
import './style.css';

export interface ISpinnerProps {
  /** Overall width/height in px. */
  size?: number;
  /** Stroke width in px. */
  thickness?: number;
}

export const Spinner: React.FC<ISpinnerProps> = ({
  size = 56,
  thickness = 8,
}) => {
  // --- Configurable visual properties ---
  const arcRatio = 0.18; // % of perimeter that becomes the visible arc (0.18 = 18%)
  const speedMs = 1400; // Rotation duration (ms)
  const trackColor = '#ffffff'; // Background ring color
  const arcColor = 'var(--green-400)'; // Foreground animated arc color

  // --- Geometry calculations ---
  // Radius considers the stroke thickness, so the arc fits correctly inside the SVG
  const r = (size - thickness) / 2;

  /**
   * Convert arcRatio to the 0–100 range used by `pathLength={100}`.
   * Clamp between 2% and 90% so the arc never becomes too small or a full circle.
   */
  const dashPct = Math.min(Math.max(arcRatio, 0.02), 0.9) * 100;

  return (
    <div
      style={{
        width: size,
        height: size,
        display: 'inline-block',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img" // announces element as graphic
        aria-label="Loading" // accessible text for assistive tech
      >
        {/* --------------------------------------------------------
            1) STATIC TRACK CIRCLE (full stroke, non-animated)
        --------------------------------------------------------- */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={trackColor}
          strokeWidth={thickness}
          strokeLinecap="round"
        />

        {/* --------------------------------------------------------
            2) ANIMATED ARC SEGMENT
            Uses strokeDasharray + rotation to animate only a portion
            of the circumference, creating a pill-shaped loader.
        --------------------------------------------------------- */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={arcColor}
          strokeWidth={thickness}
          strokeLinecap="round"
          pathLength={100} // normalize perimeter so dash array uses percentages
          strokeDasharray={`${dashPct} 100`} // 1 visible segment + 1 hidden gap
          strokeDashoffset={0}
          // CSS vars passed inline to control animation duration
          style={{ ['--dur' as string]: `${speedMs}ms` } as React.CSSProperties}
          className="spinner-arc"
        />
      </svg>
    </div>
  );
};
